import { Button, FormControlLabel, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormikProps } from 'formik';
import moment from 'moment';
import { useMemo, useRef, useState } from 'react';
import { useRecoilValueLoadable } from 'recoil';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import { FormikSwitch } from '../../../common/formik-material-ui';
import {
  trackClickedEditPowerListHours,
  trackClickedEditTargetHours,
  trackToggledPowerListUseBusinessHours,
  trackToggledPowerListUseBusinessHoursForTarget,
} from '../../../common/userTracking';
import { Business } from '../../../lib/kixie-entities/tables/Business';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { createHoursString, Scheduler } from '../CadenceSettings/Scheduler';
import { ModalWithStyles } from '../modals/ModalWithStyles';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';

const useStyles = makeStyles((theme: Theme) => ({
  webhook: {
    paddingLeft: '12px',
    width: '98.4%',
  },
  editButton: {
    maxWidth: '155px',
    maxHeight: '50px',
    minWidth: '155px',
    minHeight: '50px',
  },
  closeIcon: {
    position: 'absolute',
    top: '10px',
    right: '20px',
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    width: '18vw',
  },
  smsField: {
    minHeight: '3em',
  },
  smsWidth: {
    width: '495px',
  },
  fieldInsideRow: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    width: '110px',
  },
  timeframes: {
    marginTop: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
}));
export const dayNames = [
  'placeholder',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const cellsReset = Array.from({ length: 7 + 1 }, (i) =>
  Array.from({ length: 48 + 1 }, (j) => false)
);
export const tableLookup = Array.from({ length: 7 + 1 }, (i, dayIndex) =>
  Array.from({ length: 48 + 1 }, (j, timeIndex) => ({
    day: dayNames[dayIndex],
    time: {
      startTime: moment()
        .startOf('day')
        .add((timeIndex - 1) * 30, 'minutes')
        .format('HH:mm'),

      endTime: moment()
        .startOf('day')
        .add(timeIndex * 30 - (timeIndex === 48 ? 1 : 0), 'minutes') // last time will be 11:59 pm
        .format('HH:mm'),
    },
  }))
);

export function PowerlistHours(props: {
  formik: FormikProps<any>;
  id: number | null;
}) {
  const classes = useStyles();
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  const userLoadable = useRecoilValueLoadable<IUserAtom | Record<string, any>>(
    user
  );
  const userAtom = userLoadable.contents as IUserAtom;

  const [businessCached, businessLoadable] = makeApiRequestCached<
    Business | undefined
  >(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userAtom.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.[0] ?? cached.current
  );

  // create object that will use index as identifier
  // console.log(smsTemplateOptions, 'option sms templates');

  const schedulableHoursCached = useRef();
  const handleSetSchedulableHours = (hours: any) => {
    schedulableHoursCached.current = hours;
  };

  const powerlistHoursString = useMemo<string[]>(
    () =>
      createHoursString(
        props.formik.values.useBusinessHoursForPowerlist
          ? businessCached.current?.businessDetails?.businessHours
          : props.formik.values.powerlistHours
      ).split(','),
    [
      props.formik.values.useBusinessHoursForPowerlist,
      props.formik.values.powerlistHours,
      businessLoadable,
    ]
  );

  const targetHoursString = useMemo<string[]>(
    () =>
      createHoursString(
        props.formik.values.useBusinessHoursForTarget
          ? businessCached.current?.businessDetails?.businessHours
          : props.formik.values.targetHours
      ).split(','),
    [
      props.formik.values.useBusinessHoursForTarget,
      props.formik.values.targetHours,
      businessLoadable,
    ]
  );

  // state for business hours modal;
  const [showBusinessHours, setShowBusinessHours] = useState(false);
  const handleShowBusinessHours = () => setShowBusinessHours(true);
  const onHide = () => {
    setShowBusinessHours(false);
    setShowTargetHours(false);
  };
  // state for target hours modal
  const [showTargetHours, setShowTargetHours] = useState(false);
  const handleShowTargetHours = () => setShowTargetHours(true);

  function handleSubmit() {
    if (!createHoursString(schedulableHoursCached.current)) {
      enqueueSnackbarWithStyles({
        message: 'Please select a time range',
        alertProps: {
          severity: 'warning',
        },
      });
    } else {
      if (showBusinessHours) {
        props.formik.setFieldValue(
          'powerlistHours',
          schedulableHoursCached.current
        );
      } else {
        props.formik.setFieldValue(
          'targetHours',
          schedulableHoursCached.current
        );
      }
      onHide();
    }
  }
  return (
    <>
      <div className="container">
        <LabelledOutline label="PowerList Hours">
          <div className="row">
            <div className="col-lg-6">
              Actions will be routed:
              {powerlistHoursString.map((string, index) => (
                <div key={index}>
                  {string} <br />
                </div>
              ))}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingRight: '50px',
              }}
              className="col-lg-6"
            >
              <FormControlLabel
                control={
                  <FormikSwitch
                    name="useBusinessHoursForPowerlist"
                    formik={props.formik}
                    color="primary"
                    type="checkbox"
                  />
                }
                onClick={() => {
                  trackToggledPowerListUseBusinessHours();
                }}
                label="Use Business Hours"
              />
              <Button
                variant="contained"
                color="primary"
                className={classes.editButton}
                onClick={() => {
                  trackClickedEditPowerListHours();
                  handleShowBusinessHours();
                }}
                disabled={props.formik.values.useBusinessHoursForPowerlist}
                data-pup="edit-powerlist-hours"
              >
                Edit PowerList Hours
              </Button>
            </div>
          </div>
        </LabelledOutline>
        <div style={{ marginTop: '16px' }}>
          <LabelledOutline label="Target Hours">
            <div className="row">
              <div className="col-lg-6">
                Actions will be routed:
                {targetHoursString.map((string, index) => (
                  <div key={index}>
                    {string} <br />
                  </div>
                ))}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  paddingRight: '50px',
                }}
                className="col-lg-6"
              >
                <FormControlLabel
                  control={
                    <FormikSwitch
                      name="useBusinessHoursForTarget"
                      formik={props.formik}
                      color="primary"
                      type="checkbox"
                    />
                  }
                  onClick={() => {
                    trackToggledPowerListUseBusinessHoursForTarget();
                  }}
                  label="Use Business Hours"
                />
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.editButton}
                  onClick={() => {
                    trackClickedEditTargetHours();
                    handleShowTargetHours();
                  }}
                  disabled={props.formik.values.useBusinessHoursForTarget}
                >
                  Edit Target Hours
                </Button>
              </div>
            </div>
          </LabelledOutline>
        </div>
        <ModalWithStyles
          onSubmit={handleSubmit}
          title={showBusinessHours ? 'PowerList Hours' : 'Target Hours'}
          show={showBusinessHours || showTargetHours}
          onHide={onHide}
          isSubmitting={props.formik.isSubmitting}
          size="lg"
          dataPup="power-list-hours-save"
        >
          <Scheduler
            //if showBusinessHours is true we set cadence hours , otherwise set the target hours
            schedulableHours={
              showBusinessHours
                ? props.formik.values.powerlistHours
                : props.formik.values.targetHours
            }
            setSchedulableHours={handleSetSchedulableHours}
          />
        </ModalWithStyles>
      </div>
    </>
  );
}
